import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { networkService } from "../../config/axios";

const StatsContext = createContext(null);

const PER_PAGE = 10;

export const StatsProvider = ({ children }) => {
  // Initialize from server-rendered data (backward compatible, read-only)
  const statsData = {
    doc: window.objInit?.doc || null,
    tl: window.objInit?.tl || null,
    pdfFile: window.objInit?.pdfFile || null,
    elements: window.objInit?.elements || {},
    charts: window.objInit?.charts || [],
  };
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // KDF dropdown state
  const [kdfList, setKdfList] = useState([]);
  const [kdfListMeta, setKdfListMeta] = useState({
    currentPage: 0,
    totalCount: 0,
    hasNextPage: true,
    isLoading: false,
  });
  const [kdfSearchQuery, setKdfSearchQuery] = useState("");
  const kdfReqSeqRef = useRef(0);

  // TL dropdown state
  const [tlList, setTlList] = useState([]);
  const [tlListMeta, setTlListMeta] = useState({
    currentPage: 0,
    totalCount: 0,
    hasNextPage: true,
    isLoading: false,
  });
  const [tlSearchQuery, setTlSearchQuery] = useState("");
  const tlReqSeqRef = useRef(0);

  // Recipient dropdown state
  const [recipientList, setRecipientList] = useState([]);
  const [recipientListMeta, setRecipientListMeta] = useState({
    currentPage: 0,
    totalCount: 0,
    hasNextPage: true,
    isLoading: false,
  });
  const [recipientSearchQuery, setRecipientSearchQuery] = useState("");
  const recipientReqSeqRef = useRef(0);

  // Selected recipient ID for filtering sessions
  const [selectedRecipientId, setSelectedRecipientId] = useState(null);

  // Receiver-specific stats (fetched when a recipient is selected)
  const [receiverStats, setReceiverStats] = useState(null);
  const [receiverStatsLoading, setReceiverStatsLoading] = useState(false);
  const receiverStatsSeqRef = useRef(0);

  // Chart stats (threshold-filtered for At a Glance / Page by Page)
  const [chartStats, setChartStats] = useState(null);
  const [chartStatsLoading, setChartStatsLoading] = useState(false);
  const chartStatsSeqRef = useRef(0);

  // Language filter (docs_file_id) for filtering stats by language variant
  const [selectedDocsFileId, setSelectedDocsFileId] = useState(null);
  const selectedDocsFileIdRef = useRef(null);
  useEffect(() => { selectedDocsFileIdRef.current = selectedDocsFileId; }, [selectedDocsFileId]);

  // Load paginated KDF list for dropdown
  const loadKdfList = useCallback((page = 1, search = "") => {
    const seq = ++kdfReqSeqRef.current;
    setKdfListMeta((prev) => ({ ...prev, isLoading: true }));

    const limit = PER_PAGE;
    const offset = (page - 1) * limit;
    const params = new URLSearchParams({ limit, offset });
    if (search.trim()) params.set("query", search.trim());

    networkService(`/get_dropdown_docs.json?${params}`, "GET")
      .then((res) => {
        if (seq !== kdfReqSeqRef.current) return;
        const { data = [], meta = {} } = res.data || {};
        const items = data.map((doc) => {
          const activeLang =
            (doc.doc_files_languages || []).find((l) => l.status === "active") ||
            (doc.doc_files_languages || [])[0];
          return {
            id: doc.id,
            group_id: doc.group_id,
            title: doc.title,
            num_pages: doc.num_pages || 0,
            visits_count: doc.stats?.viewers ?? 0,
            language: activeLang?.language?.locale || "",
            language_flag: activeLang?.language || null,
            stats_path: doc.stats_path,
          };
        });
        setKdfList((prev) => (page === 1 ? items : [...prev, ...items]));
        setKdfListMeta({
          currentPage: page,
          totalCount: meta.total || 0,
          hasNextPage: meta.has_more || false,
          isLoading: false,
        });
      })
      .catch((err) => {
        if (seq !== kdfReqSeqRef.current) return;
        console.error("[StatsContext] loadKdfList error:", err);
        setKdfListMeta((prev) => ({ ...prev, isLoading: false }));
      });
  }, []);

  // Load paginated TL list for dropdown
  const loadTlList = useCallback((page = 1, search = "") => {
    const seq = ++tlReqSeqRef.current;
    setTlListMeta((prev) => ({ ...prev, isLoading: true }));

    const limit = PER_PAGE;
    const offset = (page - 1) * limit;
    const params = new URLSearchParams({ limit, offset });
    if (search.trim()) params.set("query", search.trim());

    networkService(`/links/dropdown_links.json?${params}`, "GET")
      .then((res) => {
        if (seq !== tlReqSeqRef.current) return;
        const { data = [], meta = {} } = res.data || {};
        const items = data.map((tl) => ({
          id: tl.id,
          token: tl.token,
          name: tl.name,
          domain: tl.domain,
          language_flag: tl.language_flag || null,
          language: tl.language_flag?.locale || "",
          total_counts: tl.stats?.viewers ?? 0,
          stats_path: tl.stats_path,
        }));
        setTlList((prev) => (page === 1 ? items : [...prev, ...items]));
        setTlListMeta({
          currentPage: page,
          totalCount: meta.total || 0,
          hasNextPage: meta.has_more || false,
          isLoading: false,
        });
      })
      .catch((err) => {
        if (seq !== tlReqSeqRef.current) return;
        console.error("[StatsContext] loadTlList error:", err);
        setTlListMeta((prev) => ({ ...prev, isLoading: false }));
      });
  }, []);

  // Load paginated recipient list for dropdown
  const loadRecipientList = useCallback((page = 1, search = "", token = "", type = "") => {
    const seq = ++recipientReqSeqRef.current;
    setRecipientListMeta((prev) => ({ ...prev, isLoading: true }));

    const limit = PER_PAGE;
    const offset = (page - 1) * limit;
    const params = new URLSearchParams({ limit, offset });
    if (search.trim()) params.set("query", search.trim());
    if (token) params.set("token", token);
    if (type) params.set("type", type);

    networkService(`/recipients/dropdown_recipients.json?${params}`, "GET")
      .then((res) => {
        if (seq !== recipientReqSeqRef.current) return;
        const { data = [], meta = {} } = res.data || {};
        const items = data.map((r) => ({
          id: r.id,
          name: r.name,
          email: r.email,
          percent_viewed: r.percent_viewed ?? null,
          engagement: r.engagement || null,
        }));
        setRecipientList((prev) => (page === 1 ? items : [...prev, ...items]));
        setRecipientListMeta({
          currentPage: page,
          totalCount: meta.total || 0,
          hasNextPage: meta.has_more || false,
          isLoading: false,
        });
      })
      .catch((err) => {
        if (seq !== recipientReqSeqRef.current) return;
        console.error("[StatsContext] loadRecipientList error:", err);
        setRecipientListMeta((prev) => ({ ...prev, isLoading: false }));
      });
  }, []);

  // Navigate to KDF stats page when a different KDF is selected
  const loadKdfStats = useCallback((groupId) => {
    setIsLoading(true);
    setError(null);
    window.location.href = `/kdf-stats/${groupId}#/at-a-glance`;
  }, []);

  const selectedKdfGroupId = statsData.doc?.group_id || null;
  const selectedTlToken = statsData.tl?.token || window.objInit?.tl?.token || null;

  // Fetch receiver-specific stats when selectedRecipientId changes
  const fetchReceiverStats = useCallback((receiverId) => {
    if (!receiverId) {
      setReceiverStats(null);
      return;
    }
    const seq = ++receiverStatsSeqRef.current;
    setReceiverStatsLoading(true);

    const { tl, doc } = window?.objInit || {};
    const isKdf = window.location.pathname.includes("kdf-stats");
    const type = isKdf ? "doc" : "tl";
    const token = isKdf ? doc?.group_id : tl?.token;

    const params = new URLSearchParams({
      receiver_id: receiverId,
      type,
      token,
    });

    networkService(`/statistics/receiver_stats?${params}`, "GET")
      .then((res) => {
        if (seq !== receiverStatsSeqRef.current) return;
        setReceiverStats(res.data?.stats || null);
        setReceiverStatsLoading(false);
      })
      .catch((err) => {
        if (seq !== receiverStatsSeqRef.current) return;
        console.error("[StatsContext] fetchReceiverStats error:", err);
        setReceiverStats(null);
        setReceiverStatsLoading(false);
      });
  }, []);

  // Auto-fetch when recipient selection changes
  useEffect(() => {
    fetchReceiverStats(selectedRecipientId);
  }, [selectedRecipientId, fetchReceiverStats]);

  // Fetch chart stats with a custom engagement threshold
  // docsFileId param: pass explicitly to avoid stale ref (child effects run before parent effects)
  const fetchChartStats = useCallback((threshold, docsFileId) => {
    if (!threshold) return;
    const seq = ++chartStatsSeqRef.current;
    setChartStatsLoading(true);

    const { tl, doc, pdfFile } = window?.objInit || {};
    const isKdf = window.location.pathname.includes("kdf-stats");
    const parentType = isKdf ? "doc" : "tl";
    const receiver = isKdf ? doc?.group_id : tl?.token;
    const fallbackNumPages = pdfFile?.num_pages || 0;

    // Use explicitly passed docsFileId (avoids stale ref from useEffect ordering)
    const fileId = docsFileId !== undefined ? docsFileId : selectedDocsFileIdRef.current;
    let chartUrl = `/kdf-stats-filter/chart_stats/_/${threshold}/${receiver}?parent_type=${parentType}`;
    if (fileId) {
      chartUrl += `&docs_file_id=${fileId}`;
    }

    networkService(chartUrl, "GET")
      .then((res) => {
        if (seq !== chartStatsSeqRef.current) return;
        const data = res.data;
        const charts = data.charts || {};
        const defaultStats = (tl || doc)?.stats || {};
        // Use num_pages from API (correct for selected language) or fallback
        const numPages = data.num_pages || fallbackNumPages;

        // Derive least visited pages from time_per_page
        const timePerPage = charts.time_per_page || [];
        const leastVisited = [...timePerPage]
          .filter((p) => (p.views || 0) > 0)
          .sort((a, b) => (a.views || 0) - (b.views || 0))
          .slice(0, 10)
          .map((p) => ({ page: p.page, views: p.views, page_views: p.views }));

        // Derive unviewed pages
        const viewedPages = new Set(
          timePerPage.filter((p) => p.views > 0).map((p) => p.page)
        );
        const unviewedPages = [];
        for (let i = 1; i <= numPages; i++) {
          if (!viewedPages.has(i)) {
            unviewedPages.push({ page: i, views: 0, page_views: 0 });
          }
        }

        // percent_viewed is already formatted by the backend (e.g. "25%")
        const percentViewed = data.percent_viewed;

        const mappedStats = {
          ...defaultStats,
          viewers: data.viewers,
          total_min_viewed: data.total_time,
          percent_viewed: percentViewed,
          most_visited_pages: charts.most_visited_pages || [],
          least_visited_pages: leastVisited,
          unviewed_pages: unviewedPages.slice(0, 10),
          time_per_page: timePerPage,
          returning_viewers: data.returning_viewers,
          optins_num: data.optins_num,
          engagement: data.engagement,
          by_browser: charts.by_browser || [],
          by_os: charts.by_os || [],
          by_device: charts.by_device || [],
          by_city: charts.by_city || [],
          by_subdivision: charts.by_subdivision || [],
          most_viewed_pages: data.most_viewed_pages,
        };

        setChartStats(mappedStats);
        setChartStatsLoading(false);
      })
      .catch((err) => {
        if (seq !== chartStatsSeqRef.current) return;
        console.error("[StatsContext] fetchChartStats error:", err);
        setChartStats(null);
        setChartStatsLoading(false);
      });
  }, []);

  const resetChartStats = useCallback(() => {
    setChartStats(null);
  }, []);

  return (
    <StatsContext.Provider
      value={{
        statsData,
        isLoading,
        error,
        loadKdfStats,
        kdfList,
        kdfListMeta,
        kdfSearchQuery,
        setKdfSearchQuery,
        loadKdfList,
        selectedKdfGroupId,
        tlList,
        tlListMeta,
        tlSearchQuery,
        setTlSearchQuery,
        loadTlList,
        selectedTlToken,
        recipientList,
        recipientListMeta,
        recipientSearchQuery,
        setRecipientSearchQuery,
        loadRecipientList,
        selectedRecipientId,
        setSelectedRecipientId,
        receiverStats,
        receiverStatsLoading,
        chartStats,
        chartStatsLoading,
        fetchChartStats,
        resetChartStats,
        selectedDocsFileId,
        setSelectedDocsFileId,
      }}
    >
      {children}
    </StatsContext.Provider>
  );
};

export const useStats = () => {
  const ctx = useContext(StatsContext);
  // Safe fallback when used outside StatsProvider (e.g. SideBar on non-stats pages)
  if (!ctx) {
    return {
      statsData: {
        doc: null,
        tl: null,
        pdfFile: null,
        elements: {},
        charts: [],
      },
      isLoading: false,
      error: null,
      loadKdfStats: () => {},
      kdfList: [],
      kdfListMeta: { currentPage: 0, totalCount: 0, hasNextPage: false, isLoading: false },
      kdfSearchQuery: "",
      setKdfSearchQuery: () => {},
      loadKdfList: () => {},
      selectedKdfGroupId: null,
      tlList: [],
      tlListMeta: { currentPage: 0, totalCount: 0, hasNextPage: false, isLoading: false },
      tlSearchQuery: "",
      setTlSearchQuery: () => {},
      loadTlList: () => {},
      selectedTlToken: null,
      recipientList: [],
      recipientListMeta: { currentPage: 0, totalCount: 0, hasNextPage: false, isLoading: false },
      recipientSearchQuery: "",
      setRecipientSearchQuery: () => {},
      loadRecipientList: () => {},
      selectedRecipientId: null,
      setSelectedRecipientId: () => {},
      receiverStats: null,
      receiverStatsLoading: false,
      chartStats: null,
      chartStatsLoading: false,
      fetchChartStats: () => {},
      resetChartStats: () => {},
      selectedDocsFileId: null,
      setSelectedDocsFileId: () => {},
    };
  }
  return ctx;
};

export default StatsContext;
